import { Show, createEffect, createSignal } from 'solid-js';
import { api, Customer } from '../api';
import { isOperator } from '../me';
import { ErrorLine, StatusPill, useAction } from '../ui';

const BETA_CREDITS = 10;  // a month, when Tape is switched on in the beta
const TAPE = 'https://tape.scai.fm';

/** Tape for a customer: scai.fm switches it on with a monthly allowance of credits (a take 1, an edit 0.5) and decides whether its
 *  songs may go to the customer's Studio stations. Everyone else sees what is on. */
export default function CustomerTape(props: { customer: Customer; onChanged: (c: Customer) => void }) {
  const c = () => props.customer;
  const [songs, setSongs] = createSignal(0);
  createEffect(() => setSongs(c().tape_songs_month));
  const a = useAction();
  const patch = (json: Partial<Customer>) => a.run(async () =>
    props.onChanged(await api<Customer>(`/customers/${c().id}`, { method: 'PATCH', json })));
  const save = (e: Event) => { e.preventDefault(); patch({ tape_songs_month: songs() }); };

  return (
    <Show when={isOperator() || c().tape_songs_month > 0}>
      <section aria-label="Tape" class="mb-8">
        <div class="mb-3 flex flex-wrap items-center gap-3">
          <h2 class="wordmark text-xl">Tape</h2>
          <Show when={c().tape_songs_month > 0} fallback={<StatusPill tone="off">Off</StatusPill>}>
            <span class="mono text-ink-3">{c().tape_used} of {c().tape_songs_month} credits this month</span>
          </Show>
          <Show when={isOperator()}>
            <label class="ml-auto flex items-center gap-2 text-sm text-ink-2">
              <input type="checkbox" class="toggle toggle-sm" checked={c().tape_songs_month > 0} disabled={a.busy()}
                onChange={(e) => patch({ tape_songs_month: e.currentTarget.checked ? BETA_CREDITS : 0 })} />
              Tape</label>
            <Show when={c().tape_songs_month > 0}>
              <form class="flex items-center gap-2" onSubmit={save}>
                <label class="text-sm text-ink-2" for="tape-songs">Credits a month</label>
                <input id="tape-songs" class="input input-sm input-bordered w-20" type="number" min="1" max="1000" value={songs()}
                  onInput={(e) => setSongs(Number(e.currentTarget.value))} />
                <button class="btn btn-sm" disabled={a.busy() || !songs() || songs() === c().tape_songs_month}>Save</button>
              </form>
              <label class="flex items-center gap-2 text-sm text-ink-2"
                title="Commercial use of Tape songs: keep off until the music model licence is signed">
                <input type="checkbox" class="toggle toggle-sm" checked={c().tape_to_station} disabled={a.busy()}
                  onChange={(e) => patch({ tape_to_station: e.currentTarget.checked })} />
                To stations</label>
              <label class="flex items-center gap-2 text-sm text-ink-2"
                title="scai.fm's own songs: operators send versions to the library, where they wait for review">
                <input type="checkbox" class="toggle toggle-sm" checked={c().tape_house} disabled={a.busy()}
                  onChange={(e) => patch({ tape_house: e.currentTarget.checked })} />
                scai.fm's own</label>
            </Show>
          </Show>
          <Show when={!isOperator() && c().tape_to_station}><StatusPill tone="ok">To stations</StatusPill></Show>
        </div>
        <ErrorLine error={a.error} />
        <Show when={c().tape_songs_month > 0}>
          <p class="text-sm text-ink-2">
            Songs are made at <a class="link" href={`${TAPE}/c/${c().id}`} target="_blank" rel="noopener">tape.scai.fm</a>.{' '}
            <Show when={c().tape_to_station} fallback="They can be downloaded, but not sent to the customer's stations.">
              They can be downloaded and sent to the customer's Studio stations{c().station_limit ? '' : ' (it has no station yet)'}.
            </Show>
          </p>
          <Show when={c().tape_house}>
            <p class="mt-1 text-sm text-ink-2">scai.fm's own songs: operators can send a version to the Music library, where it waits for review before it plays on scai.fm's stations.</p>
          </Show>
          <Show when={isOperator() && !c().tape_to_station}>
            <p class="mt-1 text-xs text-ink-3">Stations play in shops: commercial use. Keep it off until the music model licence is signed.</p>
          </Show>
        </Show>
      </section>
    </Show>
  );
}
